import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';

// Step sizes match PROTO's `wireDivider()` keydown handler.
const STEP = 12;
const STEP_LARGE = 40;

/** jsdom doesn't implement pointer capture, so these calls must tolerate it being absent at runtime even though the DOM lib types declare them as always present. */
type PointerCaptureCapable = {
  setPointerCapture?: (pointerId: number) => void;
  hasPointerCapture?: (pointerId: number) => boolean;
  releasePointerCapture?: (pointerId: number) => void;
};

export type PanelResizeHandleProps = {
  /** Matches `aria-orientation`. 'horizontal' resizes the HEIGHT of the panel above
   *  (drag/arrow DOWN grows); 'vertical' resizes the WIDTH of the panel to the left
   *  (drag/arrow RIGHT grows), when `side` is `'before'` (the default). */
  orientation: 'horizontal' | 'vertical';
  /** The REAL current dimension in px. Reported verbatim as aria-valuenow. */
  value: number;
  onValueChange: (next: number) => void;
  min: number;
  max: number;
  label: string;
  /** Which side of the handle the resized panel sits on. `'before'` (default) is
   *  today's behaviour — drag/arrow DOWN or RIGHT grows. `'after'` inverts the
   *  mapping — used by the Actions column in `panels/FlowPanels.tsx`, which sits
   *  to the right of its handle. */
  side?: 'before' | 'after';
  /** Double-click handler, typically "reset to default". */
  onReset?: () => void;
};

/**
 * Draggable divider that resizes a panel's height (`orientation="horizontal"`)
 * or width (`orientation="vertical"`). Generalized from PROTO's
 * `dock/PanelResizeHandle.tsx` (`edge: 'left' | 'right'`, a `sign` for which
 * side the panel sits on) — restoring the vertical axis this port originally
 * dropped when it flipped PROTO's horizontal-only dock divider into a
 * height-only one. The `side` prop is the prototype's `sign`; `'after'` is
 * the Actions column in `panels/FlowPanels.tsx`.
 * `aria-valuenow`/`min`/`max` always report the REAL dimension — PROTO's
 * `mirror()` trick (which inverted the reported value for its `edge ===
 * 'top'` case) is a bug this port fixes; it is intentionally not reproduced
 * here (bead do-NOT-create list) for either orientation.
 */
export function PanelResizeHandle({
  orientation,
  value,
  onValueChange,
  min,
  max,
  label,
  side,
  onReset,
}: PanelResizeHandleProps) {
  const isVertical = orientation === 'vertical';
  const cursorClass = isVertical ? 'cursor-col-resize' : 'cursor-row-resize';
  const [dragStart, setDragStart] = useState<{ pos: number; value: number } | null>(null);
  const sign = side === 'after' ? -1 : 1;

  const clamp = useCallback((next: number) => Math.max(min, Math.min(max, next)), [min, max]);
  const pointerPos = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => (isVertical ? e.clientX : e.clientY),
    [isVertical],
  );

  // Belt-and-suspenders: pointerup/pointercancel already clear these, but an
  // unmount mid-drag (e.g. the lens closing while the rep is still dragging)
  // must not leave the document stuck in a resize cursor. `cursorClass` is a
  // dep so a stale closure (e.g. from a prior render's orientation) can't
  // strip the wrong axis's cursor class.
  useEffect(() => {
    return () => {
      document.body.classList.remove(cursorClass, 'select-none');
    };
  }, [cursorClass]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target: PointerCaptureCapable = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    setDragStart({ pos: pointerPos(e), value });
    document.body.classList.add(cursorClass, 'select-none');
    e.preventDefault();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    const delta = pointerPos(e) - dragStart.pos;
    onValueChange(clamp(dragStart.value + sign * delta));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    setDragStart(null);
    const target: PointerCaptureCapable = e.currentTarget;
    if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture?.(e.pointerId);
    document.body.classList.remove(cursorClass, 'select-none');
  };

  // Keyboard handler stays verbatim for both orientations — Up/Left shrink,
  // Down/Right grow — when `side` is `'before'` (the default). WAI-ARIA
  // allows a separator to respond to the "secondary" arrow pair too, so the
  // horizontal case (Left/Right) sees zero behaviour change from before this
  // generalization. `side="after"` inverts both pairs via `sign`.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? STEP_LARGE : STEP;
    const grow = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? sign : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -sign : 0;
    if (grow !== 0) {
      onValueChange(clamp(value + grow * step));
      e.preventDefault();
    }
  };

  return (
    <div
      role="separator"
      aria-orientation={orientation}
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      className={clsx('group absolute inset-0 z-20 shrink-0 touch-none select-none outline-none', cursorClass)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      onDoubleClick={onReset ? () => onReset() : undefined}
    >
      <div
        className={clsx(
          'absolute bg-transparent transition-colors',
          isVertical ? 'inset-y-0 left-1/2 w-[2px] -translate-x-1/2' : 'inset-x-0 top-1/2 h-[2px] -translate-y-1/2',
          'group-hover:bg-border-action group-focus-visible:bg-border-action',
          dragStart && 'bg-border-action',
        )}
      />
    </div>
  );
}
